import { useCallback, useMemo, useState } from "react";
import styled from "styled-components";
import { Timer, TriangleAlert, Check, Ban } from "lucide-react";

import { useT } from "../_kit/i18n";
import { apiFetch } from "../_kit/api";

// Transit (day-use) booking: one date, one start time, one duration.
//
// Deliberately its own component rather than another branch inside the 1300-line overnight wizard —
// the two flows share almost nothing. Transit has no date RANGE, no per-night rate breakdown, no eZee
// hold, and one room per item; it does have a start time, a duration, and an overnight-conflict
// acknowledgement the nightly flow has no concept of.
//
// Times are :00/:30 across the full 24h so a 23:00 start (which lands the guest's end time on the
// next day) is expressible — that case is normal, not an edge.
// Which durations are on sale comes from the server (AppSetting transit.durations_hours) — it started
// as 3h/6h and moved to a single 5h option. The fallback only matters if the page is rendered without
// the prop; it is never the source of truth.
const FALLBACK_DURATIONS = [3, 6];
const SLOT_MINUTES = 30;

const Wrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
`;

const Row = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  align-items: flex-end;
`;

const Field = styled.label`
  display: flex;
  flex-direction: column;
  font-size: 0.78rem;
  font-weight: 700;
  gap: 4px;
  color: #57534e;
`;

const Input = styled.input`
  border: 1px solid #ddd8cc;
  border-radius: 8px;
  font-size: 0.9rem;
  padding: 8px 10px;
`;

const Select = styled.select`
  border: 1px solid #ddd8cc;
  border-radius: 8px;
  font-size: 0.9rem;
  padding: 8px 10px;
`;

const Button = styled.button`
  background: ${(props) => (props.$primary ? "#6d28d9" : "#ffffff")};
  border: 1px solid ${(props) => (props.$primary ? "#6d28d9" : "#ddd8cc")};
  border-radius: 8px;
  color: ${(props) => (props.$primary ? "#ffffff" : "#3f3c36")};
  cursor: pointer;
  font-size: 0.85rem;
  font-weight: 700;
  padding: 9px 16px;

  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }
`;

const WindowNote = styled.div`
  background: #faf9ff;
  border: 1px solid #e6e0fb;
  border-radius: 8px;
  color: #4c1d95;
  font-size: 0.85rem;
  font-weight: 700;
  padding: 10px 12px;
`;

const TypeCard = styled.div`
  background: ${(props) => (props.$muted ? "#faf9f7" : "#ffffff")};
  border: 1px solid #e7e3d9;
  border-radius: 10px;
  padding: 12px;
`;

const UnpricedNote = styled.div`
  color: #8a5a00;
  font-size: 0.78rem;
  line-height: 1.4;
`;

const TypeName = styled.div`
  font-size: 0.9rem;
  font-weight: 800;
  margin-bottom: 8px;
`;

const RoomBtn = styled.button`
  background: ${(props) =>
    props.$unavailable ? "#f4f2ee" : props.$selected ? "#f5f3ff" : "#ffffff"};
  border: 1px solid ${(props) => (props.$selected ? "#6d28d9" : "#ddd8cc")};
  color: ${(props) => (props.$unavailable ? "#8a857d" : "inherit")};
  text-decoration: ${(props) => (props.$unavailable ? "line-through" : "none")};
  border-radius: 8px;
  cursor: pointer;
  font-size: 0.82rem;
  font-weight: 700;
  margin: 0 6px 6px 0;
  padding: 7px 12px;
  text-align: left;
`;

const BlockedBox = styled.div`
  background: #f4f2ee;
  border: 1px solid #ddd8cc;
  border-radius: 8px;
  color: #4a4f63;
  font-size: 0.8rem;
  line-height: 1.5;
  margin-top: 8px;
  padding: 10px 12px;
`;

const ConflictBox = styled.div`
  background: #fdf8e9;
  border: 1px solid #f0e3bd;
  border-radius: 8px;
  color: #7a5a12;
  font-size: 0.8rem;
  line-height: 1.5;
  margin-top: 8px;
  padding: 10px 12px;
`;

const AckRow = styled.label`
  align-items: flex-start;
  color: #7a5a12;
  cursor: pointer;
  display: flex;
  font-size: 0.8rem;
  font-weight: 700;
  gap: 8px;
  margin-top: 8px;
`;

const ErrorText = styled.div`
  color: #b42318;
  font-size: 0.82rem;
  font-weight: 700;
`;

const startTimeOptions = () => {
  const out = [];
  for (let minutes = 0; minutes < 24 * 60; minutes += SLOT_MINUTES) {
    const h = String(Math.floor(minutes / 60)).padStart(2, "0");
    const m = String(minutes % 60).padStart(2, "0");
    out.push(`${h}:${m}`);
  }
  return out;
};

const TransitPicker = (props) => {
  const { t } = useT();
  const [date, setDate] = useState(props.date);
  const [startTime, setStartTime] = useState("09:00");
  const durations = useMemo(
    () =>
      Array.isArray(props.durations) && props.durations.length
        ? props.durations
        : FALLBACK_DURATIONS,
    [props.durations],
  );
  // The operator can retire the length that is currently selected, so the effective value is DERIVED
  // rather than synced back into state: falling back in render keeps the form from ever holding a
  // duration the server would refuse, without an extra render pass.
  const [selectedDuration, setSelectedDuration] = useState(durations[0]);
  const duration = durations.includes(selectedDuration)
    ? selectedDuration
    : durations[0];
  const [result, setResult] = useState(null);
  const [selectedRoom, setSelectedRoom] = useState(null);
  const [acknowledged, setAcknowledged] = useState(false);
  const [blockedRoom, setBlockedRoom] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const times = useMemo(() => startTimeOptions(), []);

  const search = useCallback(async () => {
    setBusy(true);
    setError(null);
    setSelectedRoom(null);
    setAcknowledged(false);
    setBlockedRoom(null);
    try {
      const body = await apiFetch(
        "/frontdesk_v2/direct_booking/start_transit",
        {
          method: "POST",
          body: JSON.stringify({
            transit_date: date,
            transit_start_time: startTime,
            duration_hours: duration,
          }),
        },
      );
      if (!body.data) {
        setError(body.message);
        setResult(null);
        return;
      }
      setResult(body.data);
    } catch {
      setError(t("error_body"));
    } finally {
      setBusy(false);
    }
  }, [date, startTime, duration, t]);

  // A room with an overnight clash is still offered — the front office decides — but it cannot be
  // submitted until the clash is explicitly acknowledged, and that acknowledgement is stored on the
  // booking line for audit.
  const needsAck = Boolean(selectedRoom?.requires_acknowledgement);
  const canSubmit =
    Boolean(selectedRoom) && (!needsAck || acknowledged) && !busy;

  const submit = useCallback(async () => {
    if (!canSubmit) return;
    setBusy(true);
    setError(null);
    try {
      const added = await apiFetch(
        "/frontdesk_v2/direct_booking/transit_items",
        {
          method: "POST",
          body: JSON.stringify({
            cart_id: result.cart_id,
            room_type_id: selectedRoom.room_type_id,
            number_of_rooms: 1,
            transit_date: date,
            transit_start_time: startTime,
            duration_hours: duration,
          }),
        },
      );
      if (!added.data) {
        setError(added.message);
        return;
      }
      const confirmed = await apiFetch(
        `/frontdesk_v2/direct_booking/items/${added.data.session_id}/confirm`,
        {
          method: "POST",
          body: JSON.stringify({
            room_ids: [selectedRoom.id],
            acknowledge_conflicts: acknowledged,
          }),
        },
      );
      if (!confirmed.data) {
        setError(confirmed.message);
        return;
      }
      props.onConfirmed({
        cartId: result.cart_id,
        sessionId: added.data.session_id,
        rateInfo: added.data.rate_info,
        cartExpiresAt: added.data.cart_expires_at,
        roomTypeId: selectedRoom.room_type_id,
        roomTypeLabel: added.data.room_type_label,
        roomId: selectedRoom.id,
        roomCode: selectedRoom.code,
        startsAt: result.starts_at,
        endsAt: result.ends_at,
        crossesMidnight: result.crosses_midnight,
        durationHours: duration,
        acknowledged: acknowledged,
      });
    } catch {
      setError(t("error_body"));
    } finally {
      setBusy(false);
    }
  }, [
    canSubmit,
    result,
    selectedRoom,
    acknowledged,
    date,
    startTime,
    duration,
    props,
    t,
  ]);

  return (
    <Wrap>
      <Row>
        <Field>
          {t("date")}
          <Input
            type="date"
            value={date}
            onChange={(event) => setDate(event.target.value)}
          />
        </Field>
        <Field>
          Jam mulai
          <Select
            value={startTime}
            onChange={(event) => setStartTime(event.target.value)}
          >
            {times.map((time) => (
              <option key={time} value={time}>
                {time}
              </option>
            ))}
          </Select>
        </Field>
        <Field>
          Durasi
          <Select
            value={duration}
            onChange={(event) =>
              setSelectedDuration(Number(event.target.value))
            }
          >
            {durations.map((hours) => (
              <option key={hours} value={hours}>
                {hours} {t("transit_hours")}
              </option>
            ))}
          </Select>
        </Field>
        <Button type="button" $primary onClick={search} disabled={busy}>
          <Timer size={14} /> Cari kamar
        </Button>
      </Row>

      {error ? <ErrorText>{error}</ErrorText> : null}

      {result ? (
        <WindowNote>
          {result.starts_at?.slice(11, 16)} → {result.ends_at?.slice(11, 16)} ·{" "}
          {result.duration_hours} {t("transit_hours")}
          {result.crosses_midnight ? ` · ${t("transit_crosses_midnight")}` : ""}
        </WindowNote>
      ) : null}

      {result && result.room_types.length === 0 ? (
        <ErrorText>
          {result.rates_unconfigured
            ? `Tarif transit ${result.duration_hours} jam belum diatur untuk tipe kamar mana pun. Atur dulu di Settings → Walk-in Room Rates.`
            : "Semua kamar sudah terpakai untuk jam tersebut. Coba jam lain."}
        </ErrorText>
      ) : null}

      {(result?.room_types || []).map((type) => (
        <TypeCard key={type.room_type_id} $muted={!type.rate_configured}>
          <TypeName>{type.room_type_label}</TypeName>
          {/* Listed even without a price, because a type that simply vanishes reads as "no rooms" and
              sends the front office hunting for a free hour that was never the problem. */}
          {type.rate_configured ? null : (
            <UnpricedNote>
              Tarif transit {type.duration_hours} jam belum diatur untuk tipe
              ini — atur di Settings → Walk-in Room Rates.
            </UnpricedNote>
          )}
          {/* An unsellable room stays VISIBLE, disabled, with the reason on hover and spelled out below
              once it is the one the FO is asking about. Hiding it left them staring at a gap in the room
              numbers with no way to tell "already sold" from "needs another 25 minutes of housekeeping". */}
          {type.rooms.map((room) => (
            <RoomBtn
              key={room.id}
              type="button"
              // NOT the `disabled` attribute: a disabled button never fires onClick, so tapping it
              // would do nothing and the front office would still be guessing. It stays clickable and
              // refuses in the handler, which is what surfaces the reason.
              aria-disabled={room.unavailable || undefined}
              title={room.unavailable_reason || undefined}
              $selected={selectedRoom?.id === room.id}
              $unavailable={room.unavailable}
              onClick={() => {
                if (room.unavailable) {
                  setBlockedRoom(room);
                  return;
                }
                setBlockedRoom(null);
                setSelectedRoom({ ...room, room_type_id: type.room_type_id });
                setAcknowledged(false);
              }}
            >
              {selectedRoom?.id === room.id ? <Check size={12} /> : null}{" "}
              {room.code}
              {room.requires_acknowledgement ? (
                <TriangleAlert size={12} color="#b45309" />
              ) : null}
              {room.unavailable ? <Ban size={12} color="#94908a" /> : null}
            </RoomBtn>
          ))}
        </TypeCard>
      ))}

      {blockedRoom ? (
        <BlockedBox>
          <b>Kamar {blockedRoom.code} tidak bisa dipakai untuk jam ini.</b>
          <div>{blockedRoom.unavailable_reason}</div>
        </BlockedBox>
      ) : null}

      {/* Not a blocker — the room IS sellable. Spacing only guarantees the housekeeping window, so a
          close neighbour leaves both guests' cards briefly live on the same door. Shown so the choice
          is made knowingly. */}
      {selectedRoom?.card_overlap_note ? (
        <BlockedBox>
          <b>Perhatian kartu — kamar {selectedRoom.code}</b>
          <div>{selectedRoom.card_overlap_note}</div>
        </BlockedBox>
      ) : null}

      {needsAck ? (
        <ConflictBox>
          <b>Kamar {selectedRoom.code} bentrok dengan tamu menginap:</b>
          {selectedRoom.conflicts.map((conflict) => (
            <div key={conflict.line_id}>· {conflict.message_id}</div>
          ))}
          <AckRow>
            <input
              type="checkbox"
              checked={acknowledged}
              onChange={(event) => setAcknowledged(event.target.checked)}
            />
            Saya mengerti kamar ini dipakai tamu lain pada jam tersebut, dan
            tetap ingin menjual slot transit ini.
          </AckRow>
        </ConflictBox>
      ) : null}

      <Row>
        <Button type="button" $primary onClick={submit} disabled={!canSubmit}>
          Kunci kamar transit
        </Button>
      </Row>
    </Wrap>
  );
};

export default TransitPicker;
